// The control for a Yes/No custom field on every surface that edits one. A stored 'true' shows ticked;
// 'false' and a value that has not been set both show unticked, because not set reads as No. Ticking saves
// 'true' and unticking saves 'false', so from here a value can change between yes and no but cannot go
// back to not set (a Dropdown field is the way to record "not answered"). A click on the box or its label
// never reaches the row behind it: a phase header row toggles the phase on click, and ticking the box must
// not also collapse it.
import React from 'react';
import {Checkbox} from '@forecast-it/design-system';
import {BOOLEAN_FALSE, BOOLEAN_TRUE} from '../forecast-app/shared/util/customFieldValueValidation';

type CustomFieldBooleanInputProps = {
	value: string | null | undefined;
	onChange: (value: string) => void;
	readOnly?: boolean;
	// The field's name. Always the checkbox's accessible name; shown beside it only with showLabel.
	label: string;
	// Show the name beside the box, on a surface that does not print it above the field.
	showLabel?: boolean;
	'data-cy'?: string;
};

export const CustomFieldBooleanInput = ({
	value,
	onChange,
	readOnly,
	label,
	showLabel,
	'data-cy': dataCy = 'custom-field-boolean-input',
}: CustomFieldBooleanInputProps): React.JSX.Element => (
	// The design-system Checkbox button already stops its own clicks. This wrapper is for a click on the
	// visible label (showLabel), which would otherwise reach the row behind.
	<span onClick={e => e.stopPropagation()}>
		<Checkbox
			aria-label={label}
			labelText={showLabel ? label : undefined}
			notBoldLabel={true}
			data-cy={dataCy}
			checked={value === BOOLEAN_TRUE}
			disabled={readOnly}
			onChange={checked => {
				if (!readOnly) onChange(checked === true ? BOOLEAN_TRUE : BOOLEAN_FALSE);
			}}
		/>
	</span>
);

export default CustomFieldBooleanInput;
